import { useState, useEffect, useRef, useCallback } from 'react';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import Select from 'react-select';
import { formatDirection, formatShipmentType } from '../../utils/shipmentHelpers';

const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

const formatMilitaryDateTime = (dateString: string | Date): string => {
  const dateStr = typeof dateString === 'string' ? dateString : dateString.toISOString();
  const cleanDateStr = dateStr.replace(/([+-]\d{2}:\d{2}|[+-]\d{4}|Z)$/, '');
  const [datePart, timePart] = cleanDateStr.includes('T')
    ? cleanDateStr.split('T')
    : cleanDateStr.split(' ');
  if (!datePart || !timePart) return dateStr;
  const [year, month, day] = datePart.split('-');
  const formattedDate = `${month}/${day}/${year}`;
  const timeOnly = timePart.split('.')[0];
  const [hours, minutes] = timeOnly.split(':');
  const militaryTime = `${hours}:${minutes}`;
  return `${formattedDate} ${militaryTime}`;
};

interface PrecheckedDriver {
  id: string;
  driverId: string;
  name: string;
  loadNumber?: string;
  direction: string;
  type: string;
  sealNumber?: string;
  trailerNumber?: string;
  arrivalTime: string;
  checkInTime?: string;
  status?: string;
  shipment?: {
    id: string;
    referenceNumber: string;
    direction?: string;
    metadata?: any;
    trailer?: {
      uuid: string;
      trailer_number?: string;
      trailer_type?: string;
      refrigerated?: boolean;
      length_feet?: number;
      weight_capacity?: number;
      volume_capacity?: number;
      carrier?: {
        id: string;
        name: string;
      };
    };
  };
}

interface PrecheckedDriversProps {
  facilityId: number | null;
  sinceHours: number;
}

const PrecheckedDrivers = ({ facilityId, sinceHours }: PrecheckedDriversProps) => {
  const [drivers, setDrivers] = useState<PrecheckedDriver[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState<'default' | 'checkin' | 'appointment'>('default');
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchPrecheckedDrivers = useCallback(async (showIndicator = false) => {
    if (!facilityId) return;

    if (showIndicator) setIsRefreshing(true);

    try {
      const authToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('authToken='))
        ?.split('=')[1] || localStorage.getItem('authToken');

      const query = `
        query GetPrecheckedDriverAssignments {
          precheckedDriverAssignments(facilityId: "${facilityId}", sinceHours: ${sinceHours}) {
            success
            message
            totalCount
            driverAssignments {
              id
              shipmentId
              driverId
              phone
              assignedAt
              checkIn
              checkOut
              status
              createdAt
              updatedAt
              driver {
                id
                firstName
                lastName
              }
              shipment {
                id
                referenceNumber
                direction
                metadata
                trailer {
                  uuid
                  trailer_number
                  trailer_type
                  refrigerated
                  length_feet
                  weight_capacity
                  volume_capacity
                  carrier {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ query })
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const assignments = result.data.precheckedDriverAssignments.driverAssignments || [];

      const transformed: PrecheckedDriver[] = assignments.map((assignment: any) => {
        const metadata = typeof assignment.shipment?.metadata === 'string'
          ? JSON.parse(assignment.shipment.metadata)
          : assignment.shipment?.metadata;

        return {
          id: assignment.id || assignment.shipmentId,
          driverId: assignment.driverId || assignment.driver?.id,
          name: assignment.driver
            ? `${assignment.driver.firstName} ${assignment.driver.lastName}`.trim()
            : 'Unknown Driver',
          loadNumber: assignment.shipment?.referenceNumber || assignment.shipmentId,
          direction: formatDirection(assignment.shipment?.direction || ''),
          type: formatShipmentType(metadata?.shipment_type),
          trailerNumber: assignment.shipment?.trailer?.trailer_number || '',
          arrivalTime: new Date(assignment.createdAt).toLocaleString(),
          checkInTime: assignment.createdAt,
          status: assignment.status,
          shipment: assignment.shipment ? {
            ...assignment.shipment,
            metadata
          } : undefined
        };
      });

      setDrivers(transformed);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch prechecked driver assignments:', err);
      setError('Failed to fetch prechecked driver assignments');
    } finally {
      if (showIndicator) {
        setTimeout(() => setIsRefreshing(false), 300);
      }
    }
  }, [facilityId, sinceHours]);

  // Initial load and refetch on dependency change
  useEffect(() => {
    if (facilityId) {
      setIsLoading(true);
      fetchPrecheckedDrivers(false).finally(() => setIsLoading(false));
    }
  }, [facilityId, sinceHours, fetchPrecheckedDrivers]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!facilityId) return;

    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(() => {
      fetchPrecheckedDrivers(false);
    }, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [facilityId, fetchPrecheckedDrivers]);

  const handleManualRefresh = () => {
    if (!isRefreshing && facilityId) {
      fetchPrecheckedDrivers(true);
    }
  };

  const getDriverTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'live load':
      case 'live':
        return 'bg-orange-100 text-orange-800';
      case 'drop':
      case 'drop off':
        return 'bg-blue-100 text-blue-800';
      case 'outbound':
        return 'bg-yellow-100 text-yellow-800';
      case 'inbound':
        return 'bg-green-100 text-green-800';
      case 'both':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const sortOptions = [
    { value: 'default', label: 'Default' },
    { value: 'checkin', label: 'Check-in Time' },
    { value: 'appointment', label: 'Appointment Time' }
  ];

  const filteredDrivers = drivers.filter(driver =>
    !searchFilter ||
    driver.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
    driver.loadNumber?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    driver.trailerNumber?.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const sortedDrivers = [...filteredDrivers].sort((a, b) => {
    if (sortBy === 'checkin') {
      return new Date(a.checkInTime || 0).getTime() - new Date(b.checkInTime || 0).getTime();
    }
    if (sortBy === 'appointment') {
      const aAppt = a.shipment?.metadata?.appointment_date;
      const bAppt = b.shipment?.metadata?.appointment_date;
      if (!aAppt && !bAppt) return 0;
      if (!aAppt) return 1;
      if (!bAppt) return -1;
      return new Date(aAppt).getTime() - new Date(bAppt).getTime();
    }
    return 0;
  });

  if (!facilityId) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a facility to view prechecked drivers.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center mt-4 w-full">
        <CircularProgress />
        <p className="mt-2">Loading prechecked drivers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-red-500">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 shadow">
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-lg">Prechecked Drivers ({sortedDrivers.length})</h2>
          <div className="flex items-center gap-2">
            {isRefreshing ? (
              <div className="flex items-center">
                <CircularProgress size={16} className="mr-2" />
                <Typography variant="caption" color="textSecondary">
                  Refreshing...
                </Typography>
              </div>
            ) : (
              <IconButton size="small" onClick={handleManualRefresh} title="Refresh">
                <Refresh fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search drivers..."
            className="border rounded p-2 text-sm flex-1"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <Select
            options={sortOptions}
            value={sortOptions.find(option => option.value === sortBy)}
            placeholder="Sort by"
            onChange={(selectedOption: any) => {
              setSortBy(selectedOption?.value || 'default');
            }}
            className="w-48"
            isSearchable={false}
          />
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-3 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        These drivers have been prechecked via geofence and are read-only. No actions can be taken from this view.
      </div>

      {sortedDrivers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No prechecked drivers found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sortedDrivers.map(driver => (
            <div
              key={driver.id}
              className="p-3 border rounded bg-gray-50 opacity-90"
            >
              <div className="flex justify-between">
                <span className="font-bold">{driver.name}</span>
                <span className={`text-xs px-2 py-1 rounded ${getDriverTypeColor(driver.type)}`}>
                  {driver.type.charAt(0).toUpperCase() + driver.type.slice(1)}
                </span>
              </div>
              <p className="text-sm text-gray-600">Ref #: {driver.loadNumber}</p>
              {driver.shipment?.trailer && (
                <div className="text-sm">
                  <p>
                    <span className="font-medium">Trailer:</span> {driver.shipment.trailer.trailer_number || 'N/A'}
                    {driver.shipment.trailer.trailer_type && (
                      <span className="text-gray-500 ml-1">({driver.shipment.trailer.trailer_type})</span>
                    )}
                  </p>
                  {driver.shipment.trailer.carrier?.name && (
                    <p>
                      <span className="font-medium">Carrier:</span> {driver.shipment.trailer.carrier.name}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {driver.direction && (
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                        {driver.direction}
                      </span>
                    )}
                    {driver.type && (
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                        {driver.type}
                      </span>
                    )}
                    {driver.shipment.trailer.refrigerated && (
                      <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                        Refrigerated
                      </span>
                    )}
                    {driver.shipment.trailer.length_feet && driver.shipment.trailer.length_feet > 0 && (
                      <span className="text-xs text-gray-500">
                        {driver.shipment.trailer.length_feet}ft
                      </span>
                    )}
                  </div>
                </div>
              )}
              {driver.shipment?.metadata?.appointment_date && (
                <p className="text-sm mt-1">
                  <span className="font-medium">Appointment:</span> {formatMilitaryDateTime(driver.shipment.metadata.appointment_date)}
                </p>
              )}
              {driver.shipment?.metadata?.appointment_must_depart_by_date && (
                <p className="text-sm">
                  <span className="font-medium">Must Depart:</span> {formatMilitaryDateTime(driver.shipment.metadata.appointment_must_depart_by_date)}
                </p>
              )}
              {driver.status && (
                <p className="text-sm">
                  <span className="font-medium">Status:</span> {driver.status}
                </p>
              )}
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>Created: {driver.arrivalTime}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrecheckedDrivers;
